import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, XIcon } from 'lucide-react';
import { PageHeader } from '../components/PageHeader';
import { galleryCategories, GalleryCategory, galleryImages } from '../data/gallery';
const EASE = [0.23, 1, 0.32, 1] as const;
export function Gallery() {
  const [category, setCategory] = useState<GalleryCategory>('All');
  const [lightbox, setLightbox] = useState<number | null>(null);
  const visible = useMemo(() => category === 'All' ? galleryImages : galleryImages.filter(image => image.category === category), [category]);
  const active = lightbox !== null ? visible[lightbox] : null;
  const step = useCallback((direction: 1 | -1) => {
    setLightbox(current => current === null ? null : (current + direction + visible.length) % visible.length);
  }, [visible.length]);
  useEffect(() => {
    if (lightbox === null) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setLightbox(null);
      if (event.key === 'ArrowRight') step(1);
      if (event.key === 'ArrowLeft') step(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightbox, step]);
  return <>
      <PageHeader eyebrow="Gallery" title="Look around Bel Air" intro="Interiors, grounds and moments from guest stays and shoots." data-id="element-580" data-mp="2fadcd9msrdc9" />

      <section className="bg-ivory-100" data-id="element-581" data-mp="37xpyxjsyjl5k">
        <div className="mx-auto max-w-[1240px] px-5 py-14 sm:px-8 lg:px-12 lg:py-20" data-id="element-582" data-mp="37wolx9sdsol7">
          <div className="flex flex-wrap gap-2" role="tablist" aria-label="Filter gallery" data-id="element-583" data-mp="2tyqd0jwtxkd3">
            {galleryCategories.map(item => <button key={item} type="button" role="tab" aria-selected={category === item} onClick={() => {
            setCategory(item);
            setLightbox(null);
          }} className="relative rounded-full px-4 py-2 text-[13px]" data-id="element-584" data-mp="1ik4cno9v81me">
                {category === item && <motion.span layoutId="gallery-filter" className="absolute inset-0 rounded-full bg-forest-700" transition={{
              duration: 0.25,
              ease: EASE
            }} data-id="element-585" data-mp="3fhsas1te3n6y" />}
                <span className={`relative transition-colors duration-150 ease-premium ${category === item ? 'text-ivory-100' : 'text-forest-600 hover:text-forest-800'}`} data-id="element-586" data-mp="36fizvau7lrt1">
                  {item}
                </span>
              </button>)}
          </div>

          <motion.div layout className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-587" data-mp="uscl5a5wq4iw">
            <AnimatePresence mode="popLayout" initial={false} data-id="element-588" data-mp="qq7vsnmhru22">
              {visible.map((image, index) => <motion.button layout key={image.src} type="button" onClick={() => setLightbox(index)} initial={{
              opacity: 0,
              scale: 0.96
            }} animate={{
              opacity: 1,
              scale: 1
            }} exit={{
              opacity: 0,
              scale: 0.96
            }} transition={{
              duration: 0.25,
              ease: EASE
            }} className={`group block w-full overflow-hidden rounded-sm text-left ${image.orientation === 'portrait' ? 'sm:row-span-2' : ''}`} data-id="element-589" data-mp="28n1dtf70x6cz" data-mpi={index}>
                  <span className="block h-full overflow-hidden bg-forest-100" data-id="element-590" data-mp="1yp58j40rp5ms">
                    <img src={image.src} alt={image.alt} loading="lazy" decoding="async" className={`h-full w-full object-cover transition-transform duration-300 ease-premium group-hover:scale-[1.04] ${image.orientation === 'portrait' ? 'aspect-[3/4] sm:aspect-auto' : 'aspect-[4/3]'}`} data-id="element-591" data-mp="23p9nuqpxvubf" />
                  </span>
                </motion.button>)}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>

      <AnimatePresence data-id="element-592" data-mp="3ilb17ji8ytwt">
        {active && <motion.div role="dialog" aria-modal="true" aria-label={active.caption} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: EASE
      }} className="fixed inset-0 z-[80] flex items-center justify-center bg-forest-900/90 p-4 sm:p-10" onClick={() => setLightbox(null)} data-id="element-593" data-mp="ghfh3itlqt4">
            <button type="button" aria-label="Previous photo" onClick={event => {
          event.stopPropagation();
          step(-1);
        }} className="absolute left-3 top-1/2 hidden h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-ivory-100/10 text-ivory-100 transition-colors duration-150 ease-premium hover:bg-gold-400 hover:text-forest-800 sm:flex" data-id="element-594" data-mp="j7qrbjg2crnv">
              <ChevronLeftIcon size={20} data-id="element-595" data-mp="su1psn74sv5q" />
            </button>
            <motion.figure key={active.src} initial={{
          opacity: 0,
          scale: 0.97
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.97
        }} transition={{
          duration: 0.22,
          ease: EASE
        }} className="max-h-full w-full max-w-4xl" onClick={event => event.stopPropagation()} data-id="element-596" data-mp="swnv0g6juyks">
              <img src={active.src} alt={active.alt} className="max-h-[75vh] w-full rounded-sm object-contain" data-id="element-597" data-mp="a20m97jewgpz" />
              <figcaption className="mt-4 flex items-center justify-between gap-4 text-sm text-ivory-200" data-id="element-598" data-mp="3sc2itall7sob">
                <span data-id="element-599" data-mp="22skpjcah8rcs">
                  <span className="font-display text-gold-300" data-id="element-600" data-mp="22sufx0zfp0j0">{active.caption}</span>
                  <span className="ml-3 text-ivory-200/60" data-id="element-601" data-mp="1y248li7g6nlo">
                    {(lightbox ?? 0) + 1} / {visible.length}
                  </span>
                </span>
                <button type="button" onClick={() => setLightbox(null)} className="inline-flex items-center gap-1.5 text-ivory-200/70 transition-colors duration-150 ease-premium hover:text-ivory-100" data-id="element-602" data-mp="1t0on5p1cyf2r">
                  <XIcon size={16} aria-hidden="true" data-id="element-603" data-mp="2gsvhyjrfqe02" />
                  Close
                </button>
              </figcaption>
            </motion.figure>
            <button type="button" aria-label="Next photo" onClick={event => {
          event.stopPropagation();
          step(1);
        }} className="absolute right-3 top-1/2 hidden h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-ivory-100/10 text-ivory-100 transition-colors duration-150 ease-premium hover:bg-gold-400 hover:text-forest-800 sm:flex" data-id="element-604" data-mp="27s72dkme85l2">
              <ChevronRightIcon size={20} data-id="element-605" data-mp="3cwueo173pxb7" />
            </button>
          </motion.div>}
      </AnimatePresence>
    </>;
}